// MARK://dhk8/pos/components/ConfirmDialog
// Modal de confirmación del design system (reemplaza window.confirm nativo).
// Mismo lenguaje visual que RefundDialog (.dhk8-* tokens, role=dialog, overlay).
//
// Uso (promise-based, drop-in para `if (!window.confirm(x)) return`):
//   const confirm = useConfirm()
//   if (!(await confirm({ title: '¿Eliminar?', message: '...', tone: 'danger' }))) return
//
// Requiere <ConfirmProvider> montado una vez en el árbol (ver App/shell raíz).

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'

export interface ConfirmOptions {
  /** Título corto del diálogo. */
  title: string
  /** Cuerpo explicativo. */
  message?: string
  /** Texto del botón de confirmar. Default "Continuar". */
  confirmLabel?: string
  /** Texto del botón de cancelar. Default "Cancelar". */
  cancelLabel?: string
  /** 'danger' pinta el botón de confirmar en rojo (acciones destructivas). */
  tone?: 'default' | 'danger'
}

type Resolver = (value: boolean) => void

const ConfirmContext = createContext<((opts: ConfirmOptions) => Promise<boolean>) | null>(null)

/** Hook: devuelve `confirm(opts) => Promise<boolean>`. */
export function useConfirm(): (opts: ConfirmOptions) => Promise<boolean> {
  const ctx = useContext(ConfirmContext)
  if (ctx === null) {
    // Fallback defensivo: si no hay provider montado, no rompemos la app —
    // caemos al confirm nativo (mejor degradar que crashear un handler).
    return (opts: ConfirmOptions) =>
      Promise.resolve(typeof window !== 'undefined' ? window.confirm(`${opts.title}${opts.message ? `\n\n${opts.message}` : ''}`) : true)
  }
  return ctx
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ opts: ConfirmOptions; resolve: Resolver } | null>(null)

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => setState({ opts, resolve }))
  }, [])

  const close = useCallback(
    (value: boolean) => {
      setState((cur) => {
        cur?.resolve(value)
        return null
      })
    },
    [],
  )

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state !== null && (
        <ConfirmDialog
          {...state.opts}
          onConfirm={() => close(true)}
          onCancel={() => close(false)}
        />
      )}
    </ConfirmContext.Provider>
  )
}

interface ConfirmDialogProps extends ConfirmOptions {
  onConfirm: () => void
  onCancel: () => void
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Continuar',
  cancelLabel = 'Cancelar',
  tone = 'default',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const confirmBtnRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    confirmBtnRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
      if (e.key === 'Enter') onConfirm()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onCancel, onConfirm])

  return (
    <div
      className="dhk8-confirm-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onCancel}
    >
      <div className="dhk8-confirm" onClick={(e) => e.stopPropagation()}>
        <div className={`dhk8-confirm__title${tone === 'danger' ? ' is-danger' : ''}`}>
          {tone === 'danger' && <AlertTriangle size={18} aria-hidden />}
          {title}
        </div>
        {message && <p className="dhk8-confirm__msg">{message}</p>}
        <div className="dhk8-confirm__actions">
          <button type="button" className="dhk8-confirm-btn" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            className={`dhk8-confirm-btn dhk8-confirm-btn--primary${tone === 'danger' ? ' dhk8-confirm-btn--danger' : ''}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
      <style>{styles}</style>
    </div>
  )
}

const styles = `
  .dhk8-confirm-overlay { position:fixed; inset:0; z-index:var(--pos-z-sheet, 240); display:flex; align-items:center; justify-content:center; padding:var(--pos-space-4,16px); background:rgba(0,0,0,.5); backdrop-filter:blur(2px); }
  .dhk8-confirm { width:100%; max-width:380px; display:flex; flex-direction:column; gap:var(--pos-space-3,12px); padding:var(--pos-space-5,20px); background:var(--pos-surface,#161C18); border:1px solid var(--pos-separator,rgba(255,255,255,.07)); border-radius:var(--pos-radius-xl,16px); box-shadow:var(--pos-shadow-3,0 16px 48px -12px rgba(0,0,0,.5)); }
  .dhk8-confirm__title { display:flex; align-items:center; gap:var(--pos-space-2,8px); font-family:var(--font-display,system-ui); font-size:var(--pos-text-lg,17px); font-weight:var(--pos-weight-semibold,600); color:var(--pos-fg,#E8EFE9); }
  .dhk8-confirm__title.is-danger { color:var(--pos-danger,#E5484D); }
  .dhk8-confirm__msg { margin:0; font-size:var(--pos-text-sm,13px); color:var(--pos-fg-tertiary,#7E8C82); line-height:1.45; }
  .dhk8-confirm__actions { display:flex; gap:var(--pos-space-2,8px); justify-content:flex-end; margin-top:var(--pos-space-1,4px); }
  .dhk8-confirm-btn { display:inline-flex; align-items:center; gap:var(--pos-space-2,8px); padding:var(--pos-space-2,8px) var(--pos-space-4,16px); min-height:var(--pos-touch-target,44px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:transparent; color:var(--pos-fg-secondary,#B6BFB8); font-size:var(--pos-text-sm,13px); font-weight:var(--pos-weight-medium,500); cursor:pointer; transition:background var(--pos-duration-fast,120ms) ease, color var(--pos-duration-fast,120ms) ease; }
  .dhk8-confirm-btn:hover { background:var(--pos-elevated,#1E2520); color:var(--pos-fg,#E8EFE9); }
  .dhk8-confirm-btn--primary { background:var(--pos-tint,#1F8B3A); border-color:var(--pos-tint,#1F8B3A); color:#fff; }
  .dhk8-confirm-btn--primary:hover { background:var(--pos-tint-strong,#1A7A33); color:#fff; }
  .dhk8-confirm-btn--danger { background:var(--pos-danger,#E5484D); border-color:var(--pos-danger,#E5484D); color:#fff; }
  .dhk8-confirm-btn--danger:hover { background:var(--pos-danger-strong,#CC3B40); color:#fff; }
`
